import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { CollaborationWorkList } from '~/data/collaboration';
import { AddWorklistDateRange } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/AddWorklistDialog/AddWorklistDateRange';
import { AddWorklistTasksTable } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/AddWorklistDialog/AddWorklistTasksTable';

import { useAddWorklistDialog } from './useAddWorklistDialog';
import { useSxStyles } from './useStyles';

type PropsType = {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  workListOptions: CollaborationWorkList[];
  employeeId: number;
  onTaskListCreate: () => void;
};

export const AddWorklistDialog = (props: PropsType) => {
  const { open, onClose, workListOptions, onTaskListCreate } = props;
  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const {
    form,
    templateOptions,
    getTaskTypeName,
    employeeData,
    handleResetTemplateTasks,
    handleSyncTemplateType,
    workListTemplateSettings,
    onSave,
    isSubmitting,
    selectedEmployeeId,
    showTasks,
    newTaskData,
  } = useAddWorklistDialog({ workListOptions, onClose, onTaskListCreate });
  const {
    control,
    formState: { isValid, errors },
  } = form;

  return (
    <Dialog open={open} scroll="paper" fullWidth maxWidth="lg">
      <SolaForceDialogTitle onClose={onClose}>
        {t('addNewWorklist')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true} sx={{ padding: '30px 24px' }}>
        <form autoComplete="off">
          <Grid container spacing={2}>
            <Grid item xs={5} alignItems={'center'}>
              <Controller
                control={control}
                name={'templateId'}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <FormControl
                      error={!!error}
                      fullWidth={true}
                      required={true}
                    >
                      <InputLabel
                        id={'selected-template-label'}
                        error={!!error}
                        size={'small'}
                        required={true}
                      >
                        {t('label_select_from_templates')}
                      </InputLabel>
                      <Select
                        id="v"
                        labelId={'selected-template-label'}
                        label={t('label_select_from_templates')}
                        value={value || ''}
                        variant={'outlined'}
                        error={!!error}
                        size={'small'}
                        onChange={(event) => {
                          const value = parseInt(`${event.target.value}`);
                          const templateId: number | undefined = isNaN(value)
                            ? undefined
                            : value;
                          onChange(templateId);
                          const { dueDate, startDate } =
                            workListTemplateSettings;
                          if (!!templateId) {
                            handleSyncTemplateType(templateId);
                            if (!!dueDate && !!startDate) {
                              handleResetTemplateTasks(
                                templateId,
                                startDate,
                                dueDate,
                              );
                            }
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxWidth: '300px',
                            },
                          },
                        }}
                      >
                        {templateOptions.map((option, index) => {
                          return (
                            <MenuItem
                              key={index}
                              value={option.fCollaborationTemplateId}
                            >
                              <ListItemText
                                primary={
                                  <>
                                    {option.fCollaborationTemplateTitle}{' '}
                                    <Chip
                                      label={
                                        getTaskTypeName(
                                          option.fCollaborationTemplateTypeFK,
                                        ) ?? ''
                                      }
                                      size="small"
                                    />
                                  </>
                                }
                                secondary={
                                  option.fCollaborationTemplateDescription
                                }
                                sx={sxStyles.templateOption}
                              />
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {!!error && (
                        <FormHelperText error={!!error}>
                          {error.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={3} alignItems={'center'}>
              <AddWorklistDateRange
                form={form}
                commonTextFieldProps={{ size: 'small' }}
                onResetTemplateTasks={(startDate, endDate) => {
                  const { id } = workListTemplateSettings;
                  if (!!id && !!startDate && !!endDate) {
                    handleResetTemplateTasks(id, startDate, endDate);
                  }
                }}
              />
            </Grid>
            <Grid item xs={4} alignItems={'center'}>
              <TextField
                value={`${employeeData?.firstName} ${employeeData?.lastName}`}
                label={t('label_collaboration_selected_employee')}
                fullWidth={true}
                disabled
                size={'small'}
              />
            </Grid>
            <Grid item xs={12}>
              {showTasks && (
                <Controller
                  control={control}
                  name={'listTasks'}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <AddWorklistTasksTable
                        employeeTaskRows={value}
                        handleTasksUpdate={onChange}
                        employeeId={selectedEmployeeId}
                        newTaskData={newTaskData}
                      />
                    );
                  }}
                />
              )}
            </Grid>
          </Grid>
          {errors?.listTasks?.message && (
            <Alert severity={'error'}>{errors?.listTasks?.message}</Alert>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
        <Button
          onClick={onSave}
          disabled={!isValid}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
